/**
 * AdminLayout - Mobile-responsive admin layout with collapsible sidebar
 * Features: Bottom navigation on mobile, slide-out drawer, responsive design
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Home, Calendar, Users, Handshake, Car, Truck, FileText, 
  BarChart3, Settings, ChevronLeft, ChevronRight,
  LogOut, User, Menu, Eye, ExternalLink, Shield, X, Bell, Globe, Radio, Link2, Wallet,
  Check
} from 'lucide-react';

import ReportButton from '../ReportButton';
import DebugModeBanner from '../DebugModeBanner';
import config from 'config';

const API_URL = config.API_URL;
const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

// Role definitions
const ROLES = {
  SUPERADMIN: 'SUPERADMIN',
  DISPATCHER: 'DISPATCHER',
  CHAUFFEUR: 'CHAUFFEUR',
};

// Menu items with role-based access and module permission mapping
const ALL_MENU_ITEMS = [
  { path: '/admin', icon: Home, label: 'Accueil', exact: true, roles: [ROLES.SUPERADMIN, ROLES.DISPATCHER, ROLES.CHAUFFEUR], module: 'dashboard', showInBottomNav: true },
  { path: '/admin/dispatch', icon: Radio, label: 'Carte en temps réel', roles: [ROLES.SUPERADMIN, ROLES.DISPATCHER], module: 'dispatch' },
  { path: '/admin/tracking-links', icon: Link2, label: 'Liens de tracking', roles: [ROLES.SUPERADMIN, ROLES.DISPATCHER], module: 'tracking' },
  { path: '/admin/planning', icon: Calendar, label: 'Planning', roles: [ROLES.SUPERADMIN, ROLES.DISPATCHER, ROLES.CHAUFFEUR], module: 'planning', showInBottomNav: true },
  { 
    path: '/admin/excursions', 
    icon: Globe, 
    label: 'Excursions', 
    roles: [ROLES.SUPERADMIN],
    module: 'excursions',
    submenu: [
      { path: '/admin/excursions', label: 'Réservations' },
      { path: '/admin/excursions/agencies', label: 'Agences' },
      { path: '/admin/excursions/guides', label: 'Guides' },
      { path: '/admin/excursions/cities', label: 'Villes' },
      { path: '/admin/excursions/experiences', label: 'Expériences Bokun' }
    ]
  },
  { path: '/admin/clients', icon: Users, label: 'Clients', roles: [ROLES.SUPERADMIN, ROLES.DISPATCHER], module: 'clients', showInBottomNav: true },
  { path: '/admin/partners', icon: Handshake, label: 'Partenaires', roles: [ROLES.SUPERADMIN], module: 'partners' },
  { path: '/admin/drivers', icon: Car, label: 'Chauffeurs', roles: [ROLES.SUPERADMIN, ROLES.DISPATCHER], module: 'drivers' },
  { path: '/admin/vehicles', icon: Truck, label: 'Véhicules', roles: [ROLES.SUPERADMIN, ROLES.DISPATCHER], module: 'vehicles' },
  { path: '/admin/billing', icon: FileText, label: 'Facturation', roles: [ROLES.SUPERADMIN], module: 'billing' },
  { path: '/admin/statistics', icon: BarChart3, label: 'Statistiques', roles: [ROLES.SUPERADMIN, ROLES.DISPATCHER], module: 'statistics', showInBottomNav: true },
  { path: '/admin/settings', icon: Settings, label: 'Paramètres', roles: [ROLES.SUPERADMIN], module: 'settings' },
];

/* ── Notification Bell Component ── */
function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/notifications/unread`, { headers: authHeaders() });
      setUnread(res.data.unread_count || 0);
    } catch {}
  }, []);

  useEffect(() => {
    fetchUnread();
    const iv = setInterval(fetchUnread, 30000);
    return () => clearInterval(iv);
  }, [fetchUnread]);

  const fetchNotifs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/notifications?limit=20`, { headers: authHeaders() });
      setNotifs(res.data.notifications || []);
    } catch {}
    setLoading(false);
  };

  const handleOpen = () => {
    setOpen(!open);
    if (!open) fetchNotifs();
  };

  const markRead = async (id) => {
    try {
      await axios.post(`${API_URL}/api/admin/notifications/${id}/read`, {}, { headers: authHeaders() });
      setNotifs(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnread(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/notifications/read-all`, {}, { headers: authHeaders() });
      setNotifs(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
    } catch {}
  };

  const CATEGORY_COLORS = {
    DRIVER: 'text-blue-400',
    DOCUMENT: 'text-amber-400',
    COMPLIANCE: 'text-red-400',
    MISSION: 'text-emerald-400',
    RH: 'text-purple-400',
    SYSTEM: 'text-white/40',
  };

  return (
    <div className="relative">
      <button onClick={handleOpen} className="p-2 text-[#E5E5E5]/60 hover:text-[#C9A24D] rounded-lg hover:bg-[#C9A24D]/10 relative" data-testid="admin-notification-bell">
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center" data-testid="notif-badge">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-96 max-h-[70vh] bg-[#1A1A1A] border border-[#C9A24D]/20 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="notif-dropdown">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <h3 className="text-white text-sm font-semibold">Notifications</h3>
              {unread > 0 && (
                <button onClick={markAllRead} className="text-[#C9A24D] text-xs hover:underline" data-testid="mark-all-read">
                  Tout marquer lu
                </button>
              )}
            </div>
            <div className="overflow-y-auto max-h-[calc(70vh-50px)]">
              {loading && <div className="text-center py-8 text-white/30 text-sm">Chargement...</div>}
              {!loading && notifs.length === 0 && <div className="text-center py-8 text-white/30 text-sm">Aucune notification</div>}
              {notifs.map(n => (
                <div key={n.id} className={`px-4 py-3 border-b border-white/5 hover:bg-white/[0.03] transition cursor-pointer ${!n.read ? 'bg-[#C9A24D]/5' : ''}`}
                  onClick={() => { if (!n.read) markRead(n.id); if (n.action_url) window.location.href = n.action_url; }}
                  data-testid={`notif-item-${n.id}`}>
                  <div className="flex items-start gap-2.5">
                    <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${n.read ? 'bg-transparent' : 'bg-[#C9A24D]'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-[9px] font-bold uppercase tracking-wider ${CATEGORY_COLORS[n.category] || 'text-white/40'}`}>{n.category}</span>
                        <span className="text-white/20 text-[9px]">{n.created_at ? new Date(n.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                      </div>
                      <p className="text-white text-xs font-medium mt-0.5">{n.title}</p>
                      <p className="text-white/50 text-[11px] mt-0.5 line-clamp-2">{n.message}</p>
                    </div>
                    {!n.read && (
                      <button onClick={(e) => { e.stopPropagation(); markRead(n.id); }} className="p-1 text-white/30 hover:text-[#C9A24D]" title="Marquer lu">
                        <Check className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}


const AdminLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    // Get user info from localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setUserInfo(user);
        
        // Extract roles - handle both array and single role formats
        const roles = user.roles || [];
        if (Array.isArray(roles)) {
          setUserRoles(roles.map(r => {
            if (typeof r === 'string') return r.toUpperCase();
            if (typeof r === 'object' && r.name) return r.name.toUpperCase();
            return String(r).toUpperCase();
          }));
        } else if (typeof roles === 'string') {
          setUserRoles([roles.toUpperCase()]);
        } else if (typeof roles === 'object' && roles.name) {
          setUserRoles([roles.name.toUpperCase()]);
        } else if (user.role) {
          setUserRoles([user.role.toUpperCase()]);
        } else {
          setUserRoles([ROLES.CHAUFFEUR]);
        }
      } catch (e) {
        console.error('Error parsing user info:', e);
      }
    }
  }, []);

  const hasAccess = (item) => {
    // SUPERADMIN always has full access
    if (userRoles.includes(ROLES.SUPERADMIN)) return true;
    // Check role-based access first
    const roleOk = item.roles.some(role => userRoles.includes(role));
    if (!roleOk) return false;
    // Ticket 6.1: Check module-level permission from user's permissions array
    if (item.module) {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          const perms = userData.permissions;
          if (Array.isArray(perms) && perms.length > 0) {
            return perms.includes(item.module);
          }
        } catch (e) { /* ignore */ }
      }
    }
    return roleOk;
  };

  const menuItems = ALL_MENU_ITEMS.filter(hasAccess);
  const bottomNavItems = menuItems.filter(item => item.showInBottomNav).slice(0, 5);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('token');
    localStorage.removeItem('ygo_token');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const getRoleLabel = () => {
    if (userRoles.includes(ROLES.SUPERADMIN)) return 'Super Admin';
    if (userRoles.includes(ROLES.DISPATCHER)) return 'Dispatcher';
    if (userRoles.includes(ROLES.CHAUFFEUR)) return 'Chauffeur';
    return userInfo?.role || 'Utilisateur';
  };

  const getRoleBadgeColor = () => {
    if (userRoles.includes(ROLES.SUPERADMIN)) return 'bg-purple-500/20 text-purple-400';
    if (userRoles.includes(ROLES.DISPATCHER)) return 'bg-blue-500/20 text-blue-400';
    return 'bg-green-500/20 text-green-400';
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B] flex">
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Desktop: fixed, Mobile: slide-out drawer */}
      <aside 
        className={`
          fixed left-0 top-0 h-full bg-[#1F1F1F] border-r border-[#C9A24D]/20 z-50 
          transition-all duration-300 ease-in-out
          ${collapsed ? 'w-16' : 'w-64'}
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
      >
        {/* Logo / Brand */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-[#C9A24D]/20">
          {!collapsed && (
            <span className="text-[#C9A24D] font-bold text-lg">Y GO Admin</span>
          )}
          {/* Close button on mobile */}
          <button
            onClick={() => {
              if (window.innerWidth < 1024) {
                setMobileMenuOpen(false);
              } else {
                setCollapsed(!collapsed);
              }
            }}
            className="p-2 text-[#E5E5E5]/60 hover:text-[#C9A24D] hover:bg-[#C9A24D]/10 rounded-lg transition-colors"
          >
            <span className="lg:hidden"><X className="w-5 h-5" /></span>
            <span className="hidden lg:block">
              {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
            </span>
          </button>
        </div>

        {/* User Profile + Logout */}
        <div className={`p-4 border-b border-[#C9A24D]/20 ${collapsed ? 'px-2' : ''}`}>
          <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
            <div className="w-10 h-10 rounded-full bg-[#C9A24D] flex items-center justify-center text-[#0B0B0B] font-bold flex-shrink-0">
              {userInfo?.first_name?.[0] || userInfo?.email?.[0]?.toUpperCase() || 'A'}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-[#E5E5E5] font-medium text-sm truncate">
                  {userInfo?.first_name || 'Admin'}
                </p>
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${getRoleBadgeColor()}`}>
                  <Shield className="w-2.5 h-2.5" />
                  {getRoleLabel()}
                </span>
              </div>
            )}
          </div>
          <button
            onClick={handleLogout}
            className={`flex items-center gap-2 mt-3 px-3 py-1.5 rounded-lg w-full text-red-400 hover:bg-red-500/10 transition-colors text-xs ${
              collapsed ? 'justify-center px-2' : ''
            }`}
            title={collapsed ? 'Déconnexion' : undefined}
          >
            <LogOut className="w-3.5 h-3.5 flex-shrink-0" />
            {!collapsed && <span className="font-medium">Déconnexion</span>}
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-2 flex-1 overflow-y-auto max-h-[calc(100vh-200px)]">
          <ul className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path, item.exact);
              
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                      active 
                        ? 'bg-[#C9A24D] text-[#0B0B0B]' 
                        : 'text-[#E5E5E5]/70 hover:bg-[#C9A24D]/10 hover:text-[#E5E5E5]'
                    } ${collapsed ? 'justify-center px-2' : ''}`}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-[#0B0B0B]' : ''}`} />
                    {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* View Site */}
        <div className="p-2 border-t border-[#C9A24D]/20 space-y-1">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg w-full text-[#C9A24D] hover:bg-[#C9A24D]/10 transition-colors ${
              collapsed ? 'justify-center px-2' : ''
            }`}
            title={collapsed ? 'Voir le site' : undefined}
          >
            <Eye className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span className="text-sm font-medium">Voir le site</span>}
          </a>
        </div>
      </aside>

      {/* Main Content */}
      <main 
        className={`
          flex-1 transition-all duration-300 ease-in-out 
          pb-16 lg:pb-0
          ${collapsed ? 'lg:ml-16' : 'lg:ml-64'}
        `}
      >
        {/* Top Header Bar */}
        <div className="h-14 bg-[#1F1F1F] border-b border-[#C9A24D]/20 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30">
          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 text-[#E5E5E5] rounded-lg hover:bg-[#C9A24D]/10 lg:hidden"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          
          {/* Logo on mobile */}
          <span className="text-[#C9A24D] font-bold text-lg lg:hidden">Y GO</span>
          
          {/* Spacer for desktop */}
          <div className="hidden lg:block" />
          
          {/* Right side actions */}
          <div className="flex items-center gap-2">
            {/* Notifications bell */}
            <NotificationBell />
            
            {/* View Site - Hidden on mobile (in sidebar) */}
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg bg-[#C9A24D]/10 text-[#C9A24D] hover:bg-[#C9A24D]/20 transition-colors"
              title="Voir le site"
              data-testid="view-site-btn"
            >
              <Eye className="w-4 h-4" />
              <span className="text-sm font-medium hidden md:inline">Voir le site</span>
              <ExternalLink className="w-3 h-3 hidden md:block" />
            </a>
          </div>
        </div>

        {/* Page Content */}
        <div className="min-h-[calc(100vh-56px)]">
          <DebugModeBanner />
          {children}
        </div>
      </main>

      {/* Bottom Navigation - Mobile Only */}
      <nav className="fixed bottom-0 left-0 right-0 h-16 bg-[#1F1F1F] border-t border-[#C9A24D]/20 z-30 lg:hidden safe-area-pb">
        <div className="flex items-center justify-around h-full px-2">
          {bottomNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path, item.exact);
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center flex-1 h-full gap-1 transition-colors ${
                  active ? 'text-[#C9A24D]' : 'text-[#E5E5E5]/50'
                }`}
              >
                <Icon className={`w-5 h-5 ${active ? 'text-[#C9A24D]' : ''}`} />
                <span className="text-[10px] font-medium truncate max-w-[60px]">{item.label}</span>
              </Link>
            );
          })}
          
          {/* More menu button */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="flex flex-col items-center justify-center flex-1 h-full gap-1 text-[#E5E5E5]/50"
          >
            <Menu className="w-5 h-5" />
            <span className="text-[10px] font-medium">Plus</span>
          </button>
        </div>
      </nav>
      <ReportButton tokenKey="access_token" />
    </div>
  );
};

export default AdminLayout;
